import { useTranslation } from "react-i18next";

export type EnumGroup =
  | "clearance"
  | "assetType"
  | "massnahmeStatus"
  | "ereignisStatus"
  | "risikoStatus"
  | "priority"
  | "ciaLevel"
  | "riskClass"
  | "treatment"
  | "role"
  | "auditOp"
  | "auditEntity";

/**
 * A minimal stand-in for react-i18next's TFunction — just enough surface for
 * resolveEnumLabel below. useTranslation()'s real t is typed against the
 * literal keys known statically from the German catalogue, which a
 * dynamically-built `enums.<group>.<value>` key never is, so bridging the two
 * needs a cast (isolated to useEnumLabel's adapter closure below). Expressing
 * the dependency as this plain type, rather than the real TFunction, is what
 * lets tests pass an ordinary stub instead of rendering a component.
 */
type Translate = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Pure lookup logic behind useEnumLabel(), factored out so it is testable
 * directly with a stub translator instead of needing to render a component
 * just to reach react-i18next's useTranslation().
 *
 * A value with no catalogue entry renders as itself rather than as a raw
 * key, so a new enum member is visibly untranslated instead of broken.
 */
export function resolveEnumLabel(t: Translate, group: EnumGroup, value: string | null | undefined): string {
  if (!value) return "—";
  return t(`enums.${group}.${value}`, { defaultValue: value });
}

/**
 * Labels for values that are stored in the database — most groups as the
 * German word itself (clearance, status, …), a couple as English/technical
 * literals hand-listed rather than read from a shared schema: auditOp has a
 * schema (apps/api/src/db/schema.ts), but only inside apps/api, which this
 * package must not import; auditEntity has no schema anywhere; it is a plain
 * `text("entity")` column, populated ad hoc per API module.
 *
 * Either way, the values themselves never change — they are in the audit
 * log's entity/op columns and payloads, and clearance is compared against
 * the documentation's classifications — so the key is the stored value
 * verbatim and only the label is translated.
 */
export function useEnumLabel() {
  const { t } = useTranslation();
  return {
    label(group: EnumGroup, value: string | null | undefined): string {
      // The dynamically-built key is never one of the literal keys i18next's
      // own typing knows about — see the Translate comment above.
      return resolveEnumLabel((key, options) => t(key as never, options), group, value);
    },
  };
}
